/**
 * FILTRI SYSTEM - Modulo Filtri Ottimizzato
 * Sostituisce le 15+ chiamate a applyGlobalFilters con sistema intelligente
 */

import { eventManager, Events } from '../../core/events.js';
import { resolveStudioPlayerMantraRoles } from '../../main/studioCalc.js';
import { debounce as createDebounced } from '../../utils/debounce.js';

/** Reparto colore (allineato a docs/redesign/fantapp/fa-data.js ROLE_GROUP) */
export const ROLE_MACRO_GROUP = {
  P: 'por',
  POR: 'por',
  DS: 'dif',
  DC: 'dif',
  DD: 'dif',
  B: 'dif',
  E: 'dif',
  M: 'cen',
  C: 'cen',
  W: 'cen',
  T: 'att',
  A: 'att',
  PC: 'att',
};

function roleMacroKey(role) {
  return String(role ?? '')
    .trim()
    .toUpperCase();
}

export function getRoleMacroGroup(role) {
  return ROLE_MACRO_GROUP[roleMacroKey(role)] || 'cen';
}

export function getRoleFilterChipClass(role) {
  return `role-filter role-filter--${getRoleMacroGroup(role)}`;
}

export function getRoleChipClass(role) {
  return `role-chip role-chip--${getRoleMacroGroup(role)}`;
}
import { log } from '../../utils/logger.js';
import { playerToKey } from '../../utils/playerKey.js';

/**
 * True se il player_key è nell'insieme uscite asta (Set o Map).
 * @param {object} player
 * @param {Map|Set|null|undefined} auctionExits
 */
export function isAuctionExited(player, auctionExits) {
  const key = playerToKey(player);
  if (!key || !auctionExits) return false;
  if (auctionExits instanceof Set) return auctionExits.has(key);
  if (auctionExits instanceof Map) return auctionExits.has(key);
  return false;
}

/**
 * True se il target Studio del giocatore è segnato uscito (legacy; non usato dal filtro live).
 * @param {object} player
 * @param {Map<string, { sold?: boolean }>|null|undefined} studioTargets
 */
export function isStudioTargetExited(player, studioTargets) {
  const key = playerToKey(player);
  if (!key || !studioTargets) return false;
  const target = studioTargets.get(key);
  return Boolean(target && target.sold);
}

/**
 * True se il giocatore è non disponibile in live: in roster OPPURE in auction_exits.
 * @param {object} player
 * @param {{ auctionExits?: Map|Set, roster?: Map }} ctx
 */
export function isPlayerUnavailableInLive(player, ctx = {}) {
  const key = playerToKey(player);
  if (key && ctx.roster instanceof Map && ctx.roster.has(key)) return true;
  if (isAuctionExited(player, ctx.auctionExits)) return true;
  return false;
}

/**
 * Predicato lista Svincolati: in live nasconde gli usciti/presi salvo toggle «Mostra usciti».
 * @param {object} player
 * @param {{ auctionMode?: string, showExited?: boolean, auctionExits?: Map|Set, roster?: Map }} ctx
 */
export function passesExitedListFilter(player, ctx = {}) {
  const auctionMode = ctx.auctionMode === 'live' ? 'live' : 'prep';
  if (auctionMode !== 'live') return true;
  if (ctx.showExited) return true;
  return !isPlayerUnavailableInLive(player, ctx);
}

/**
 * True se il giocatore è già in Studio (target o bozza con dati).
 * @param {object} player
 * @param {{ studioTargets?: Map, studioDrafts?: Map }} ctx
 */
export function isPlayerInStudio(player, ctx = {}) {
  const key = playerToKey(player);
  if (!key) return false;
  if (ctx.studioTargets instanceof Map && ctx.studioTargets.has(key)) return true;
  if (ctx.studioDrafts instanceof Map && ctx.studioDrafts.has(key)) return true;
  return false;
}

/**
 * Lista Svincolati: opzione «Nascondi in Studio».
 */
export function passesHideStudioListFilter(player, ctx = {}) {
  if (!ctx.hideStudio) return true;
  return !isPlayerInStudio(player, ctx);
}

class FiltersManager {
  constructor() {
    this.filters = {
      search: '',
      roles: [],
      showExited: false,
      hideStudio: false,
    };

    /** @type {{ auctionMode: 'prep'|'live', auctionExits: Set|null, roster: Map|null, studioTargets: Map|null, studioDrafts: Map|null }} */
    this.listContext = {
      auctionMode: 'prep',
      auctionExits: null,
      roster: null,
      studioTargets: null,
      studioDrafts: null,
    };
    
    this.allPlayers = [];
    this.filteredPlayers = [];
    this.isProcessing = false;
    
    // Debounce per performance
    this.debouncedApply = this.debounce(this.applyFilters.bind(this), 150);
  }

  /**
   * Imposta i dati dei giocatori
   */
  setPlayers(players) {
    this.allPlayers = players || [];
    this.filteredPlayers = [...this.allPlayers];
    log.info('Filtri: Dati giocatori impostati', this.allPlayers.length);
  }

  /**
   * Aggiorna un singolo filtro
   */
  setFilter(name, value) {
    if (this.filters[name] === value) return; // Nessun cambio
    
    this.filters[name] = value;
    log.debug('Filtro aggiornato:', name, value);
    
    // Applica con debounce
    this.debouncedApply();
    
    // Emetti evento
    eventManager.emit(Events.FILTERS_CHANGE, {
      filter: name,
      value,
      filters: { ...this.filters }
    });
  }

  /**
   * Ottiene il valore di un filtro
   */
  getFilter(name) {
    return this.filters[name];
  }

  /**
   * Reset tutti i filtri
   */
  setListContext({ auctionMode, auctionExits, roster, studioTargets, studioDrafts } = {}) {
    if (auctionMode === 'live' || auctionMode === 'prep') {
      this.listContext.auctionMode = auctionMode;
    }
    if (auctionExits instanceof Set || auctionExits instanceof Map || auctionExits === null) {
      this.listContext.auctionExits = auctionExits;
    }
    if (roster instanceof Map || roster === null) {
      this.listContext.roster = roster;
    }
    if (studioTargets instanceof Map || studioTargets === null) {
      this.listContext.studioTargets = studioTargets;
    }
    if (studioDrafts instanceof Map || studioDrafts === null) {
      this.listContext.studioDrafts = studioDrafts;
    }
  }

  resetFilters() {
    this.filters = {
      search: '',
      roles: [],
      showExited: false,
      hideStudio: false,
    };
    
    this.debouncedApply();
    eventManager.emit(Events.FILTERS_RESET, { filters: { ...this.filters } });
    log.info('Filtri: Reset completato');
  }

  /**
   * Verifica se un giocatore corrisponde ai ruoli filtro (ruoli Mantra canonici).
   */
  matchesRoleFilter(player, filterRoles) {
    if (filterRoles.length === 0) return true;
    const playerRoles = resolveStudioPlayerMantraRoles(player);
    if (!playerRoles.length) return false;
    return filterRoles.some((filterRole) => playerRoles.includes(filterRole));
  }

  /**
   * Applica tutti i filtri (OTTIMIZZATO)
   */
  applyFilters() {
    if (this.isProcessing || this.allPlayers.length === 0) return;
    
    this.isProcessing = true;
    log.time('Filtri: Applicazione');
    
    const { search, roles, showExited, hideStudio } = this.filters;
    const listCtx = {
      auctionMode: this.listContext.auctionMode,
      showExited,
      hideStudio,
      auctionExits: this.listContext.auctionExits,
      roster: this.listContext.roster,
      studioTargets: this.listContext.studioTargets,
      studioDrafts: this.listContext.studioDrafts,
    };
    const applyExitedFilter =
      listCtx.auctionMode === 'live' && !showExited;
    const applyHideStudioFilter = Boolean(hideStudio);

    if (!search && roles.length === 0 && !applyExitedFilter && !applyHideStudioFilter) {
      this.filteredPlayers = [...this.allPlayers];
      this.isProcessing = false;
      log.timeEnd('Filtri: Applicazione');
      this.emitFilteredResults();
      return;
    }

    // Applica filtri in sequenza ottimizzata
    this.filteredPlayers = this.allPlayers.filter(player => {
      // Filtro ricerca (più veloce per primo)
      if (search && !this.matchesSearch(player, search)) {
        return false;
      }
      
      // Filtro ruoli (con mappatura per compatibilità)
      if (!this.matchesRoleFilter(player, roles)) {
        return false;
      }

      if (!passesExitedListFilter(player, listCtx)) {
        return false;
      }

      if (!passesHideStudioListFilter(player, listCtx)) {
        return false;
      }
      
      return true;
    });

    this.isProcessing = false;
    log.timeEnd('Filtri: Applicazione');
    log.info('Filtri: Risultati', {
      totali: this.allPlayers.length,
      filtrati: this.filteredPlayers.length,
      filtri: this.filters
    });
    
    this.emitFilteredResults();
  }

  /**
   * Verifica match ricerca
   */
  matchesSearch(player, searchTerm) {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    return Boolean(player.name && player.name.toLowerCase().includes(term));
  }

  /**
   * Emette i risultati filtrati
   */
  emitFilteredResults() {
    eventManager.emit(Events.TABLE_UPDATE, {
      players: this.filteredPlayers,
      total: this.allPlayers.length,
      filtered: this.filteredPlayers.length
    });
  }

  /**
   * Debounce utility
   */
  debounce(func, delay) {
    return createDebounced(func, delay);
  }

  /**
   * Ottieni risultati filtrati
   */
  getFilteredPlayers() {
    return this.filteredPlayers;
  }

  /**
   * Ottieni stato filtri
   */
  getFiltersState() {
    return { ...this.filters };
  }

  /**
   * Alias per resetFilters (compatibilità)
   */
  reset() {
    this.resetFilters();
  }
}

// Singleton globale
export const filtersManager = new FiltersManager();
